import React, {
	createRef,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { List } from "react-virtualized";
import useData from "../../hooks/useData";
import accessibilityProvider from "../../utils/accessibilityProvider";
import BrandRow from "./BrandRow";
import { WidgetCardLoadMore } from ".";
import { MODES } from "../../conf";
import message from "../../utils/message";
import { MESSAGE_TYPES } from "../../hooks/useMessage";
import useMode from "../../hooks/useMode";
import useDimensions from "../../hooks/useDimensions";
import inPreview from "../../utils/inPreview";

const INF_SCROLL_BUFFER = 2;
const WidgetCardBody = memo(
	({ rowCount, getContainerHeight, getHeaderHeight, getContainerWidth }) => {
		const { mode, inPPE } = useMode();
		const { innerWidth } = useDimensions();

		const listRef = useRef(null);
		const listHeightRef = useRef(null);

		const listRowHeightRef = useRef([]);

		const {
			l: brands,
			self_products,
			moreLoading,
			canLoadMore,
			loadMoreBrands,
			lastLoadCount,
			moreError,
			feedHasUpsell,
		} = useData();

		const [brandRowRefs, setBrandRowRefs] = useState([
			...Array(rowCount).map(() => createRef()),
		]);

		useEffect(() => {
			setBrandRowRefs((brandRowRefs) => {
				const currLength = brandRowRefs.length;
				const numAdditions = Math.max(0, rowCount - currLength);

				return [
					...brandRowRefs,
					...Array(numAdditions).map(() => createRef()),
				];
			});
		}, [rowCount, feedHasUpsell]);

		const [listHeight, setListHeight] = useState(0);
		const [listWidth, setListWidth] = useState(0);

		const handleRowRender = useCallback(
			(e) => {
				if (!inPPE || inPreview) return;
				// console.log("lastLoadCount :>> ", lastLoadCount);
				if (
					e.stopIndex >= rowCount - INF_SCROLL_BUFFER &&
					!moreLoading &&
					!moreError &&
					lastLoadCount !== 0
				)
					loadMoreBrands();
			},
			[
				inPPE,
				loadMoreBrands,
				moreLoading,
				moreError,
				rowCount,
				lastLoadCount,
			]
		);

		const getHeightParams = useCallback(() => {
			let maxHeight = 0;
			let avgHeight = 0;
			let minHeight = Infinity;

			let numNonZeroHeights = 0;

			brandRowRefs.forEach((brandRowRef) => {
				const height =
					brandRowRef?.getBoundingClientRect()?.height || 0;
				numNonZeroHeights += height ? 1 : 0;
				maxHeight = Math.max(maxHeight, height);
				avgHeight += Math.max(0, height);
				minHeight = Math.min(minHeight, Math.max(0, height));
			});

			avgHeight = avgHeight / (numNonZeroHeights || 1);

			return {
				max: maxHeight,
				avg: avgHeight,
				min: minHeight,
			};
		}, [brandRowRefs]);

		const rowHeight = useCallback(
			({ index }) => {
				const adjustedBrandIndex = feedHasUpsell ? index - 1 : index;

				// load more button
				if (adjustedBrandIndex === brands.length) return 65;

				const baselineHeight = 240;
				const paddingBuffer = 10;

				if (index < 0) return 0;

				let rowHeight = 0;
				if (brandRowRefs?.[index]) {
					const height =
						brandRowRefs[index].getBoundingClientRect().height;
					if (height) listRowHeightRef.current[index] = height;
					rowHeight = height || listRowHeightRef.current[index];
				}
				return (rowHeight || baselineHeight) + paddingBuffer;
			},
			[feedHasUpsell, brands, brandRowRefs]
		);

		const rowRenderer = useCallback(
			({ key, index, style, isVisible, isScrolling }) => {
				const adjustedBrandIndex = feedHasUpsell ? index - 1 : index;
				const isUpsellRender = feedHasUpsell && index === 0;

				const brand = isUpsellRender
					? self_products
					: brands?.[adjustedBrandIndex];

				const childAccessibilityProvider = accessibilityProvider({
					role: "cell",
					disableTabIndex: true,
				});

				const setBrandRef = (el) => {
					setBrandRowRefs((brandRowRefs) => {
						if (!el || brandRowRefs[index]) {
							return brandRowRefs;
						}
						return [
							...brandRowRefs.slice(0, index),
							el,
							...brandRowRefs.slice(index + 1),
						];
					});
				};

				const defaultProps = {
					setBrandRef,
					brand,
					upsell: isUpsellRender,
					idx: index,
					isVisible,
					isParentScrolling: isScrolling,
					...childAccessibilityProvider,
				};

				return (
					<div
						key={key}
						style={style}
						{...accessibilityProvider({
							role: "row",
							disableTabIndex: true,
						})}
					>
						{brand ? (
							<BrandRow {...defaultProps} />
						) : adjustedBrandIndex === brands.length ? (
							<WidgetCardLoadMore
								childAccessibilityProvider={
									childAccessibilityProvider
								}
							/>
						) : null}
					</div>
				);
			},
			[brands, feedHasUpsell, self_products]
		);

		useEffect(() => {
			const height = getContainerHeight();
			if (mode !== MODES.INLINE) return;
			message(MESSAGE_TYPES.RESIZE, {
				height,
			});
		}, [getContainerHeight, mode, listHeight]);

		useEffect(() => {
			const containerHeight = getContainerHeight() || 0;
			const { avg } = getHeightParams();
			if (mode === MODES.INLINE) {
				// need to have height to show all rows
				const reqHeight = Math.max(400, rowCount * avg);
				// console.warn("TRIGGER HEIGHT COMPUTE", reqHeight);
				setListHeight(reqHeight);
				let newHeight = 0;
				for (let i = 0; i <= rowCount; i++) {
					newHeight += rowHeight({ index: i });
				}
				setListHeight(newHeight);
				return;
			}

			// PPE Widget = 75% height => dynamic for diff viewport, configure listHeight accordingly
			let reqHeight =
				Math.max(400, containerHeight) - (getHeaderHeight() || 0);

			if (Math.abs(listHeightRef.current - reqHeight) < 10) return;
			listHeightRef.current = reqHeight;
			setListHeight(reqHeight);
		}, [
			getHeightParams,
			getContainerHeight,
			rowCount,
			rowHeight,
			mode,
			getHeaderHeight,
		]);

		useEffect(() => {
			const reqWidth =
				getContainerWidth() || Math.min(600, innerWidth - 8);
			setListWidth(reqWidth);
		}, [getContainerWidth, innerWidth]);

		useEffect(() => {
			if (!brandRowRefs[0]) {
				return;
			}
			listRef.current.recomputeRowHeights();
		}, [brandRowRefs]);

		const renderedRowCount = useMemo(
			() => (canLoadMore || moreLoading ? rowCount + 1 : rowCount),
			[rowCount, moreLoading, canLoadMore]
		);

		return (
			<div className="widget-card-body">
				<List
					height={listHeight}
					width={listWidth}
					rowCount={renderedRowCount}
					rowHeight={rowHeight}
					rowRenderer={rowRenderer}
					onRowsRendered={handleRowRender}
					ref={listRef}
					{...accessibilityProvider({
						name: "recommended products section",
					})}
				></List>
			</div>
		);
	}
);

export default WidgetCardBody;
